import { Fragment } from "react";
import type { Route } from "next";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import {
  LogOut,
  Mails,
  Moon,
  NotepadText,
  Settings,
  Shield,
  Sun,
  User,
} from "lucide-react";

import { MENU_SIDEBAR_MAIN } from "@/config/layout-30.config";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export function SidebarContent() {
  const t = useTranslations("layout30");
  const pathname = usePathname();
  const { resolvedTheme, setTheme } = useTheme();
  const isLight = resolvedTheme !== "dark";

  return (
    <>
      <ScrollArea className="h-[calc(100vh-12.5rem)] w-full grow lg:h-[calc(100vh-5.5rem)]">
        <nav
          aria-label={t("primaryNavigation")}
          className="flex grow shrink-0 flex-col items-center gap-1"
        >
          {MENU_SIDEBAR_MAIN.map((item) => {
            const label = t(item.title! as Parameters<typeof t>[0]);
            const selected =
              item.path === pathname ||
              Boolean(
                item.rootPath &&
                item.rootPath !== "/" &&
                pathname.startsWith(`${item.rootPath}/`),
              );
            const icon = item.icon ? <item.icon className="size-4.5!" /> : null;

            return (
              <Fragment key={item.title}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {item.path ? (
                      <Button
                        asChild
                        variant="ghost"
                        mode="icon"
                        data-state={selected ? "open" : undefined}
                        className={cn(
                          "size-9 shrink-0 rounded-md border border-transparent",
                          "hover:text-primary data-[state=open]:border-input",
                        )}
                      >
                        <Link href={item.path as Route} aria-label={label}>
                          {icon}
                        </Link>
                      </Button>
                    ) : (
                      <Button
                        disabled
                        variant="ghost"
                        mode="icon"
                        aria-label={label}
                        className="size-9 shrink-0 rounded-md border border-transparent"
                      >
                        {icon}
                      </Button>
                    )}
                  </TooltipTrigger>
                  <TooltipContent side="right">{label}</TooltipContent>
                </Tooltip>
                {item.separator ? <Separator className="my-1 w-6.5" /> : null}
              </Fragment>
            );
          })}
        </nav>
      </ScrollArea>

      <div className="flex shrink-0 flex-col items-center gap-1">
        <Button
          disabled
          variant="ghost"
          mode="icon"
          aria-label={t("controls.messagesUnavailable")}
          className="text-muted-foreground hover:text-foreground"
        >
          <Mails className="size-4.5! opacity-100" />
        </Button>
        <Button
          disabled
          variant="ghost"
          mode="icon"
          aria-label={t("controls.notesUnavailable")}
          className="text-muted-foreground hover:text-foreground"
        >
          <NotepadText className="size-4.5! opacity-100" />
        </Button>
        <Button
          variant="ghost"
          mode="icon"
          onClick={() => setTheme(isLight ? "dark" : "light")}
          aria-label={t("theme.toggle")}
        >
          <Moon className="size-4.5! dark:hidden" />
          <Sun className="hidden size-4.5! dark:block" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger
            className="cursor-pointer py-2.5"
            aria-label={t("account.openMenu")}
          >
            <Avatar className="size-7">
              <AvatarFallback>{t("account.initials")}</AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="mb-4 w-64"
            side="right"
            align="start"
            sideOffset={11}
          >
            <div className="flex items-center gap-3 px-3 py-2">
              <Avatar>
                <AvatarFallback>{t("account.initials")}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-start">
                <span className="text-sm font-semibold text-foreground">
                  {t("account.product")}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t("account.sessionUnavailable")}
                </span>
                <Badge
                  variant="info"
                  appearance="outline"
                  size="sm"
                  className="mt-1"
                >
                  {t("phaseValue")}
                </Badge>
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <User />
              <span>{t("account.profileUnavailable")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              <Settings />
              <span>{t("account.preferencesUnavailable")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              <Shield />
              <span>{t("account.securityUnavailable")}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <LogOut />
              <span>{t("account.signOutUnavailable")}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
